import type { FileRef } from '@/types/file';
import { apiClient } from './client';
import type { StaffOrganization } from '@/types';

// =============================================================================
// Types
// =============================================================================

interface StaffOrganizationsResponse {
  organizations: StaffOrganization[];
  currentOverride: number | null;
}

export interface PremiumItem {
  id: number;
  title: string;
  summary?: string;
  published?: string;
  duration?: number;
}

/** One page of the paginated premium-content endpoints. */
export interface PremiumContentPage {
  count: number;
  results: PremiumItem[];
}

/** Query params for the paginated premium-content endpoints. */
export interface PremiumContentParams {
  search?: string;
  limit: number;
  offset: number;
}

export interface ReplaceActiveContentPayload {
  /** Share whose active content will be replaced (apiClient → `share_id`). */
  shareId: number;
  /** New active packet (packet-typed shares only; apiClient → `source_packet_id`). */
  sourcePacketId?: number;
  /** New active checklist (checklist-typed shares only; apiClient → `source_checklist_id`). */
  sourceChecklistId?: number;
}

export interface ReplaceActiveContentResponse {
  success: boolean;
  /** Set when the share is packet-typed (apiClient ← `packet_id`). */
  packetId?: number;
  /** Set when the share is checklist-typed (apiClient ← `checklist_id`). */
  checklistId?: number;
}

export interface OrgUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

export type ItemType = 'packet' | 'checklist';

/** One item to grant (apiClient → `{ item_type, item_id }`). */
export interface GrantItemRef {
  itemType: ItemType;
  itemId: number;
}

export interface GrantItemsPayload {
  items: GrantItemRef[];
  /** Target organization (apiClient → `organization_id`). */
  organizationId: number;
}

/** One granted item in the response (apiClient ← `{ item_id, item_type, share_id }`). */
export interface GrantResult {
  itemId: number;
  itemType: ItemType;
  shareId: number;
}

export interface GrantItemsResponse {
  granted: GrantResult[];
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Fetch all organizations for staff org selector.
 * Only accessible to staff users.
 */
export async function getStaffOrganizations(): Promise<StaffOrganizationsResponse> {
  const response = await apiClient.get<StaffOrganizationsResponse>(
    '/api/staff/organizations/'
  );
  return response.data;
}

/**
 * Fetch one page of premium published packets/checklists for the content
 * library. Both endpoints are offset-paginated and return
 * `{ count, results }` (the `Pagination_PremiumContentItem_` envelope).
 */
export async function getStaffPremiumContent(
  itemType: ItemType,
  params: PremiumContentParams
): Promise<PremiumContentPage> {
  const path =
    itemType === 'packet'
      ? '/api/staff/premium-packets/'
      : '/api/staff/premium-checklists/';
  const response = await apiClient.get<PremiumContentPage>(path, { params });
  return response.data;
}

/**
 * Grant premium items to an organization by copying them.
 */
export async function grantPremiumItems(
  payload: GrantItemsPayload
): Promise<GrantItemsResponse> {
  const response = await apiClient.post<GrantItemsResponse>(
    '/api/staff/grant-items/',
    payload
  );
  return response.data;
}

/**
 * Replace a share's active content with a premium packet/checklist.
 * Copies the source item and swaps it in as the share's active content.
 */
export async function replaceActiveContent(
  payload: ReplaceActiveContentPayload
): Promise<ReplaceActiveContentResponse> {
  const response = await apiClient.post<ReplaceActiveContentResponse>(
    '/api/staff/replace-active-content/',
    payload
  );
  return response.data;
}

// =============================================================================
// Premium Toggle (vingapi)
// =============================================================================

export interface TogglePremiumPayload {
  itemType: ItemType;
  itemId: number;
}

export interface TogglePremiumResponse {
  success: boolean;
  premium: boolean;
  itemType: ItemType;
  itemId: number;
}

/**
 * Toggle premium status for a packet or checklist.
 * Staff-only endpoint.
 */
export async function togglePremium(
  payload: TogglePremiumPayload
): Promise<TogglePremiumResponse> {
  const response = await apiClient.post<TogglePremiumResponse>(
    '/api/staff/toggle-premium/',
    payload
  );
  return response.data;
}

// =============================================================================
// Staff Management Types
// =============================================================================

// Network types
export interface NetworkAccess {
  contacts: boolean;
  packets: boolean;
  checklists: boolean;
  requirements: boolean;
  activityExportEnabled: boolean;
  activityExportStartDate: string | null;
  activityExportLastGenerated: string | null;
  seatsExportEnabled: boolean;
  seatsExportLastGenerated: string | null;
}

/**
 * The slice of `NetworkResponse.settings` (`SettingsSchema`) the network pane
 * edits. Deliberately narrow: the pane reads nothing else (#761 / vingapi#1093).
 */
export interface NetworkSettings {
  /** Resolved: Ving support contacts visible in every member org that does not override it. */
  peopleShowInternalContacts: boolean;
}

export interface Network {
  id: number;
  name: string;
  identifier: string;
  access: NetworkAccess;
  settings: NetworkSettings;
}

export interface NetworkOrganization {
  id: number;
  name: string;
  identifier: string;
  seats: number | null;
  joinedAt: string;
}

export interface NetworkUser {
  id: number;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'viewer';
  isStaffProvisioned: boolean;
  networks: UserNetworkMembership[];
  organizations: UserOrgMembership[];
  contactMethods: UserContactMethods;
}

// Organization types
export interface OrganizationAccess {
  contacts: boolean | null;
  packets: boolean | null;
  checklists: boolean | null;
  requirements: boolean | null;
}

export interface OrganizationSettings {
  generalTimezone: string;
  generalContactNameFormat: string;
  reminderDays: string;
  reminderHours: string;
  importRequireContactMethod: boolean;
  scoringPassingThreshold: number;
  /** Resolved (#761 / vingapi#1093): Ving support contacts visible in People and counts. */
  peopleShowInternalContacts: boolean;
  /**
   * Per field: true when the org row is NULL and the value above is inherited
   * (from `settingsInheritedFrom`, else the default), false when the org
   * overrides it. A key is absent when the API predates the field, and the
   * whole map is absent on an even older build (deploy window, #806).
   */
  inherited?: Record<string, boolean>;
}

/** The network an org's inherited settings come from (its first active membership). */
export interface SettingsInheritedFrom {
  id: number;
  /** Nullable on the wire (`SettingsInheritedFrom.name: string | null`); fall back to `identifier`. */
  name: string | null;
  identifier: string;
}

export interface Organization {
  id: number;
  name: string;
  identifier: string;
  logo: FileRef | null;
  access: OrganizationAccess;
  settings: OrganizationSettings;
  /** null when the org is standalone (inherited values are the defaults). */
  settingsInheritedFrom: SettingsInheritedFrom | null;
}

export interface OrganizationUser {
  id: number;
  firstName: string;
  lastName: string;
  level: 'admin' | 'manager' | 'viewer';
  isActive: boolean;
  isStaffProvisioned: boolean;
  contactId: number | null;
  canEditLogo: boolean;
  networks: UserNetworkMembership[];
  organizations: UserOrgMembership[];
  contactMethods: UserContactMethods;
}

// Search types
export interface UserNetworkMembership {
  id: number;
  name: string;
  role: string;
}

export interface UserOrgMembership {
  id: number;
  name: string | null;
  level: string;
}

export interface UserContactMethods {
  active: string[];
  inactive: string[];
  bad: string[];
}

export interface UserSearchResult {
  id: number;
  firstName: string;
  lastName: string;
  networks: UserNetworkMembership[];
  organizations: UserOrgMembership[];
  contactMethods: UserContactMethods;
  /**
   * Fields below are hand-extended ahead of the vingapi side of #223 chunk 2
   * and are OPTIONAL on purpose: `/api/staff/users/search/` does not return
   * them yet. The merge diff renders whatever is present and says so when a
   * count is missing, so nothing here may become required without also giving
   * the UI a value for every caller.
   */
  isActive?: boolean;
  /** ISO timestamp of the account's last login, or null if it never logged in. */
  lastLogin?: string | null;
  /** Assignments the account would carry into a merge. */
  assignmentCount?: number;
  /** Submissions the account would carry into a merge. */
  submissionCount?: number;
}

export interface OrganizationSearchResult {
  id: number;
  name: string;
  identifier: string;
  logo: FileRef | null;
}

// Input types
export interface CreateNetworkInput {
  name: string;
  identifier: string;
}

export interface UpdateNetworkInput {
  name?: string;
  identifier?: string;
}

export interface UpdateNetworkAccessInput {
  contacts?: boolean;
  packets?: boolean;
  checklists?: boolean;
  requirements?: boolean;
  activityExportEnabled?: boolean;
  activityExportStartDate?: string | null;
  seatsExportEnabled?: boolean;
}

export interface AddOrganizationInput {
  organizationId?: number;
  name?: string;
  identifier?: string;
  seats?: number | null;
}

export interface UpdateNetworkOrganizationInput {
  seats: number | null;
}

export interface AddUserToNetworkInput {
  userId?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  role: 'admin' | 'manager' | 'viewer';
}

export interface UpdateNetworkUserInput {
  role: 'admin' | 'manager' | 'viewer';
}

export interface CreateOrganizationInput {
  name: string;
  identifier: string;
}

export interface UpdateOrganizationInput {
  name?: string;
  identifier?: string;
}

export interface UpdateOrganizationAccessInput {
  contacts?: boolean | null;
  packets?: boolean | null;
  checklists?: boolean | null;
  requirements?: boolean | null;
}

export interface UpdateOrganizationSettingsInput {
  generalTimezone?: string;
  generalContactNameFormat?: string;
  reminderDays?: string;
  reminderHours?: string;
  importRequireContactMethod?: boolean;
  scoringPassingThreshold?: number;
  /** `null` clears the org override so the value inherits again (apiClient keeps the null). */
  peopleShowInternalContacts?: boolean | null;
}

export interface UpdateNetworkSettingsInput {
  /** Applies to every member org that does not override it. */
  peopleShowInternalContacts?: boolean;
}

export interface AddUserToOrgInput {
  userId?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  level: 'admin' | 'manager' | 'viewer';
}

export interface UpdateOrganizationUserInput {
  level?: 'admin' | 'manager' | 'viewer';
  isActive?: boolean;
  canEditLogo?: boolean;
}

// =============================================================================
// Network API Functions
// =============================================================================

/**
 * Create a new network.
 */
export async function createNetwork(input: CreateNetworkInput): Promise<Network> {
  const response = await apiClient.post<Network>('/api/staff/networks/', input);
  return response.data;
}

/**
 * Get network details including access flags.
 */
export async function getNetwork(networkId: number): Promise<Network> {
  const response = await apiClient.get<Network>(`/api/staff/networks/${networkId}/`);
  return response.data;
}

/**
 * Update network details.
 */
export async function updateNetwork(networkId: number, input: UpdateNetworkInput): Promise<Network> {
  const response = await apiClient.patch<Network>(`/api/staff/networks/${networkId}/`, input);
  return response.data;
}

/**
 * Update network access flags.
 */
export async function updateNetworkAccess(networkId: number, input: UpdateNetworkAccessInput): Promise<Network> {
  const response = await apiClient.patch<Network>(`/api/staff/networks/${networkId}/access/`, input);
  return response.data;
}

/**
 * Update network settings (`PATCH /api/staff/networks/{id}/settings/`).
 */
export async function updateNetworkSettings(
  networkId: number,
  input: UpdateNetworkSettingsInput
): Promise<Network> {
  const response = await apiClient.patch<Network>(`/api/staff/networks/${networkId}/settings/`, input);
  return response.data;
}

// =============================================================================
// Network Organization API Functions
// =============================================================================

/** Standard offset-paginated list envelope (matches the backend Pagination[T]). */
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** Query params shared by the staff list endpoints (apiClient snake-cases keys). */
export interface StaffListParams {
  search?: string;
  limit?: number;
  offset?: number;
}

/**
 * List organizations in a network (paginated + searchable).
 * Search matches organization name or identifier.
 */
export async function getNetworkOrganizations(
  networkId: number,
  params: StaffListParams = {},
): Promise<Paginated<NetworkOrganization>> {
  const response = await apiClient.get<Paginated<NetworkOrganization>>(
    `/api/staff/networks/${networkId}/organizations/`,
    { params },
  );
  return response.data;
}

/**
 * Add organization to network (link existing or create new).
 */
export async function addOrganizationToNetwork(
  networkId: number,
  input: AddOrganizationInput
): Promise<NetworkOrganization> {
  const response = await apiClient.post<NetworkOrganization>(
    `/api/staff/networks/${networkId}/organizations/`,
    input
  );
  return response.data;
}

/**
 * Update organization membership (seats).
 */
export async function updateNetworkOrganization(
  networkId: number,
  orgId: number,
  input: UpdateNetworkOrganizationInput
): Promise<NetworkOrganization> {
  const response = await apiClient.patch<NetworkOrganization>(
    `/api/staff/networks/${networkId}/organizations/${orgId}/`,
    input
  );
  return response.data;
}

/**
 * Remove organization from network.
 */
export async function removeOrganizationFromNetwork(networkId: number, orgId: number): Promise<void> {
  await apiClient.delete(`/api/staff/networks/${networkId}/organizations/${orgId}/`);
}

// =============================================================================
// Network User API Functions
// =============================================================================

/**
 * List users with network access (paginated + searchable).
 * Search matches user name or account email.
 */
export async function getNetworkUsers(
  networkId: number,
  params: StaffListParams = {},
): Promise<Paginated<NetworkUser>> {
  const response = await apiClient.get<Paginated<NetworkUser>>(
    `/api/staff/networks/${networkId}/users/`,
    { params },
  );
  return response.data;
}

/**
 * Add user to network (link existing or provision new).
 */
export async function addUserToNetwork(
  networkId: number,
  input: AddUserToNetworkInput
): Promise<NetworkUser> {
  const response = await apiClient.post<NetworkUser>(
    `/api/staff/networks/${networkId}/users/`,
    input
  );
  return response.data;
}

/**
 * Update user's network role.
 */
export async function updateNetworkUser(
  networkId: number,
  userId: number,
  input: UpdateNetworkUserInput
): Promise<NetworkUser> {
  const response = await apiClient.patch<NetworkUser>(
    `/api/staff/networks/${networkId}/users/${userId}/`,
    input
  );
  return response.data;
}

/**
 * Remove user from network.
 */
export async function removeUserFromNetwork(networkId: number, userId: number): Promise<void> {
  await apiClient.delete(`/api/staff/networks/${networkId}/users/${userId}/`);
}

// =============================================================================
// Organization API Functions
// =============================================================================

/**
 * Create a new organization.
 */
export async function createOrganization(input: CreateOrganizationInput): Promise<Organization> {
  const response = await apiClient.post<Organization>('/api/staff/organizations/', input);
  return response.data;
}

/**
 * Get organization details including access and settings.
 */
export async function getOrganization(orgId: number): Promise<Organization> {
  const response = await apiClient.get<Organization>(`/api/staff/organizations/${orgId}/`);
  return response.data;
}

/**
 * Update organization details.
 */
export async function updateOrganization(orgId: number, input: UpdateOrganizationInput): Promise<Organization> {
  const response = await apiClient.patch<Organization>(`/api/staff/organizations/${orgId}/`, input);
  return response.data;
}

/**
 * Update organization access flags.
 */
export async function updateOrganizationAccess(
  orgId: number,
  input: UpdateOrganizationAccessInput
): Promise<Organization> {
  const response = await apiClient.patch<Organization>(`/api/staff/organizations/${orgId}/access/`, input);
  return response.data;
}

/**
 * Update organization settings.
 */
export async function updateOrganizationSettings(
  orgId: number,
  input: UpdateOrganizationSettingsInput
): Promise<Organization> {
  const response = await apiClient.patch<Organization>(`/api/staff/organizations/${orgId}/settings/`, input);
  return response.data;
}

// =============================================================================
// Organization User API Functions
// =============================================================================

/**
 * List users in an organization (paginated + searchable).
 * Search matches user name or account email; `includeInactive` keeps
 * deactivated members in the result.
 */
export async function getOrganizationUsers(
  orgId: number,
  params: StaffListParams & { includeInactive?: boolean } = {},
): Promise<Paginated<OrganizationUser>> {
  const response = await apiClient.get<Paginated<OrganizationUser>>(
    `/api/staff/organizations/${orgId}/users/`,
    { params },
  );
  return response.data;
}

/**
 * Add user to organization (link existing or provision new).
 */
export async function addUserToOrganization(
  orgId: number,
  input: AddUserToOrgInput
): Promise<OrganizationUser> {
  const response = await apiClient.post<OrganizationUser>(
    `/api/staff/organizations/${orgId}/users/`,
    input
  );
  return response.data;
}

/**
 * Update user's organization level or status.
 */
export async function updateOrganizationUser(
  orgId: number,
  userId: number,
  input: UpdateOrganizationUserInput
): Promise<OrganizationUser> {
  const response = await apiClient.patch<OrganizationUser>(
    `/api/staff/organizations/${orgId}/users/${userId}/`,
    input
  );
  return response.data;
}

/**
 * Deactivate user in organization.
 */
export async function deactivateOrganizationUser(orgId: number, userId: number): Promise<void> {
  await apiClient.delete(`/api/staff/organizations/${orgId}/users/${userId}/`);
}

// =============================================================================
// Staff force-merge (#223 chunk 2, M1/M4)
// =============================================================================

/** Body of `POST /api/users/merge/force/`. */
export interface ForceMergeUsersInput {
  /** The account that survives. */
  primaryId: number;
  /** The account that is absorbed and then permanently deleted. */
  duplicateId: number;
}

/** What the merge actually moved onto the surviving account. */
export interface ForceMergeMovedCounts {
  seats: number;
  methods: number;
  assignments: number;
  submissions: number;
}

/** 200 body of `POST /api/users/merge/force/`. */
export interface ForceMergeUsersResponse {
  mergedUserId: number;
  deletedUserId: number;
  moved: ForceMergeMovedCounts;
}

/**
 * Staff-only force merge of two user accounts.
 *
 * `POST /api/users/merge/force/` — 403 for a non-staff caller, 409 when either
 * account is already mid-merge. The client's interceptors snake_case the
 * request body and camelCase the response, so `primary_id` / `merged_user_id`
 * are handled for us.
 */
export async function forceMergeUsers(
  input: ForceMergeUsersInput,
): Promise<ForceMergeUsersResponse> {
  const response = await apiClient.post<ForceMergeUsersResponse>(
    '/api/users/merge/force/',
    input,
  );
  return response.data;
}

// =============================================================================
// Search API Functions
// =============================================================================

/**
 * Search users by email, phone, or name.
 */
export async function searchUsers(query: string, limit = 10): Promise<UserSearchResult[]> {
  const response = await apiClient.get<UserSearchResult[]>('/api/staff/users/search/', {
    params: { q: query, limit },
  });
  return response.data;
}

/**
 * Search organizations by name or identifier.
 */
export async function searchOrganizations(
  query: string,
  excludeNetworkId?: number,
  limit = 10
): Promise<OrganizationSearchResult[]> {
  const response = await apiClient.get<OrganizationSearchResult[]>('/api/staff/organizations/search/', {
    params: { q: query, excludeNetworkId, limit },
  });
  return response.data;
}
